var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import useIsMobileLayout from "@hooks/useIsMobileLayout";
import { parseComponentId, toBool, canCustomisePagesAndDashboards } from "@utils";
import { UXPContext } from "@core/context/Context";
import { splitLink } from "@core/layout/navigationUtils";
import ComponentRenderer from "@helper/ComponentRenderer/ComponentRenderer";
import ViewNotFound from "@helper/ComponentRenderer/ViewNotFound";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import { ViewSkeleton } from "@widget/loaders";
// lazy: PageEditor pulls the widget drawer (dashboard chunk) - only rendered
// while a page is actually being edited, so it has no place in main.js
import { lazyComponent } from "../../../../lazy";
const PageEditor = lazyComponent(() => import(/* webpackChunkName: "dashboard" */ "@core/pages/page-editor/PageEditor"));
import { useRouterContext, useSearchParams } from "@hooks/useRouteHandlers";
import { getAllPluggableViews, onRegistryChange } from "@core/pages/pluggable-view/pluggableViewRegistry";
import { getMountedObjectTypes, onMountedTabsChange } from "@core/pages/injected-tabs/mountedInjectedTabsRegistry";
import { registerFloatingActionSection, unregisterFloatingActionSection, FloatingActionSectionType } from "@core/floating-actions/floatingActionsRegistry";
import NotificationBlock from "@helper/NotificationBlock";
import ButtonComponent from "@widget/buttons/ButtonComponent";
// A protected navigation link owns the page at its URL: only a YML sync repoints it.
const PROTECTED_PAGE_ERROR = 'This page is system-managed (protected) and cannot be changed here.';
const ConfiguredPageBase = ({ pageId, additionalProps, configuredProps, notFoundMessage, disablePageEditor = false }) => {
    var _a;
    const context = useContext(UXPContext);
    const { navigate, generateURL, location } = useRouterContext();
    const { configurepage } = useSearchParams();
    const componentInfo = useMemo(() => parseComponentId(pageId), [pageId]);
    const [isEditing, setIsEditing] = useState(false);
    const [hasPluggableViews, setHasPluggableViews] = useState(false);
    const [mountedObjectTypes, setMountedObjectTypes] = useState([]);
    const [isMainPageOverridden, setIsMainPageOverridden] = useState(false);
    const [isMainPageProtected, setIsMainPageProtected] = useState(false);
    const [isMainPageCustom, setIsMainPageCustom] = useState(false);
    // Tri-state: undefined while it is still being resolved. Nothing editor-related is rendered or
    // registered until it is known, so a protected page never flashes an editor. The answer is
    // stamped with the path it was resolved for and read back through `pageProtection` below, so a
    // client-side navigation invalidates it DURING the render that changes the URL — an effect
    // would run a frame too late and the pencil would blink on the new page.
    const [resolvedProtection, setResolvedProtection] = useState(null);
    const currentPath = (_a = location === null || location === void 0 ? void 0 : location.pathname) !== null && _a !== void 0 ? _a : '';
    const pageProtection = resolvedProtection && resolvedProtection.path === currentPath ? resolvedProtection.isProtected : undefined;
    const canCustomize = !context.isPublicSession && canCustomisePagesAndDashboards(context);
    const hasEditableContent = !!pageId && !disablePageEditor;
    // Phones can't configure pages or dashboards: edit links open them read-only
    const isMobile = useIsMobileLayout();
    useEffect(() => {
        setIsEditing(!isMobile && hasEditableContent && configurepage == '1');
    }, [configurepage, hasEditableContent, isMobile]);
    const resolveCurrentPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const fullPath = (_a = location === null || location === void 0 ? void 0 : location.pathname) !== null && _a !== void 0 ? _a : '';
        const partialPath = fullPath.startsWith('/view') ? fullPath.slice('/view'.length) : fullPath;
        // A master link with a page owns its URL — the server derives that URL's route from the link,
        // over any custom route on the same path — so the edit has to land on the link. Only when no
        // link serves the page does a custom route decide the page, and a page-less link comes last.
        const { data: navData } = yield context.executeServiceSafe('System', 'AccountNavigation:GetAll', {}, { json: true });
        const navLinks = Array.isArray(navData) ? navData : [];
        // A link's URL may carry a query string (parameters the link opens the page with) — the
        // page is identified by the PATH, so match on that.
        const navLink = navLinks.find(nl => splitLink(nl.Link).path === fullPath);
        const asNavLink = () => ({
            type: 'navlink',
            key: navLink.Key,
            id: navLink.ID,
            isModified: toBool(navLink.IsModified) && navLink.Source === 'yml',
            // Made in Master Links, not shipped by an app's YAML: its page is its own setting,
            // with no default behind it to reset to. The server treats an empty Source as YAML.
            isCustom: !!navLink.Source && navLink.Source !== 'yml',
            // A protected link is system-managed: only a YML sync changes what it points at.
            isProtected: toBool(navLink.Protected),
        });
        if ((navLink === null || navLink === void 0 ? void 0 : navLink.Key) && navLink.PageID)
            return asNavLink();
        const { data: routesData } = yield context.executeServiceSafe('System', 'AccountRoute:All', {}, { json: true });
        const routes = Array.isArray(routesData) ? routesData : [];
        const customRoute = routes.find(r => r.Route === partialPath);
        if (customRoute === null || customRoute === void 0 ? void 0 : customRoute.Key) {
            const { data: ymlRoutesData } = yield context.executeServiceSafe('System', 'GetRoutesFromConfigurations', { ExcludeOverridden: '0' }, { json: true });
            const ymlRoutes = Array.isArray(ymlRoutesData) ? ymlRoutesData : [];
            const ymlRoute = ymlRoutes.find(r => r.Route === fullPath);
            const isModified = !!(ymlRoute === null || ymlRoute === void 0 ? void 0 : ymlRoute.PageID) && customRoute.PageID !== ymlRoute.PageID;
            // a route no app's YAML defines: nothing to reset to
            return { type: 'route', key: customRoute.Key, isModified, isProtected: false, isCustom: !(ymlRoute === null || ymlRoute === void 0 ? void 0 : ymlRoute.PageID) };
        }
        if (navLink === null || navLink === void 0 ? void 0 : navLink.Key)
            return asNavLink();
        return null;
    }), [context, location === null || location === void 0 ? void 0 : location.pathname]);
    // Is this URL system-managed? A master link flagged Protected owns it, otherwise the YML route
    // at this path decides (its flags are inherited from the app - spec section 5). A custom DB route
    // is never protected: the table has no such column, and a protected URL refuses one anyway.
    useEffect(() => {
        if (!canCustomize || !hasEditableContent) {
            setResolvedProtection(null);
            return;
        }
        let cancelled = false;
        const fullPath = currentPath;
        const resolved = (isProtected) => {
            if (!cancelled)
                setResolvedProtection({ path: fullPath, isProtected });
        };
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const { data: navData } = yield context.executeServiceSafe('System', 'AccountNavigation:GetAll', {}, { json: true });
            const navLinks = Array.isArray(navData) ? navData : [];
            // Path only — see resolveCurrentPage: the query belongs to the link, not the URL's identity.
            const navLink = navLinks.find(nl => splitLink(nl.Link).path === fullPath);
            if (navLink) {
                resolved(toBool(navLink.Protected));
                return;
            }
            const { data: ymlRoutesData } = yield context.executeServiceSafe('System', 'GetRoutesFromConfigurations', { ExcludeOverridden: '0' }, { json: true });
            const ymlRoutes = Array.isArray(ymlRoutesData) ? ymlRoutesData : [];
            const ymlRoute = ymlRoutes.find(r => r.Route === fullPath);
            resolved(!!ymlRoute && (toBool(ymlRoute.Protected) || toBool(ymlRoute.LockAccess)));
        }))();
        return () => { cancelled = true; };
    }, [canCustomize, hasEditableContent, context, currentPath]);
    useEffect(() => {
        if (!isEditing) {
            setIsMainPageOverridden(false);
            setIsMainPageProtected(false);
            setIsMainPageCustom(false);
            return;
        }
        resolveCurrentPage().then(rec => {
            var _a, _b, _c;
            setIsMainPageOverridden((_a = rec === null || rec === void 0 ? void 0 : rec.isModified) !== null && _a !== void 0 ? _a : false);
            setIsMainPageProtected((_b = rec === null || rec === void 0 ? void 0 : rec.isProtected) !== null && _b !== void 0 ? _b : false);
            setIsMainPageCustom((_c = rec === null || rec === void 0 ? void 0 : rec.isCustom) !== null && _c !== void 0 ? _c : false);
        });
    }, [isEditing, resolveCurrentPage]);
    const handleSaveMainPage = useCallback((newPageId, newConfiguredProps, extraArgs) => __awaiter(void 0, void 0, void 0, function* () {
        const rec = yield resolveCurrentPage();
        if (!rec)
            return { error: "This page's configuration is read-only (defined in YML)." };
        if (rec.isProtected)
            return { error: PROTECTED_PAGE_ERROR };
        if (rec.type === 'route') {
            return context.executeServiceSafe('System', 'AccountRoute:UpdateRoute', Object.assign({ Key: rec.key, PageID: newPageId, ConfiguredProps: JSON.stringify(newConfiguredProps !== null && newConfiguredProps !== void 0 ? newConfiguredProps : {}) }, extraArgs));
        }
        return context.executeServiceSafe('System', 'AccountNavigation:UpdateLink', Object.assign({ Key: rec.key, PageID: newPageId, ConfiguredProps: JSON.stringify(newConfiguredProps !== null && newConfiguredProps !== void 0 ? newConfiguredProps : {}) }, extraArgs));
    }), [context, resolveCurrentPage]);
    const handleResetMainPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const fullPath = (_a = location === null || location === void 0 ? void 0 : location.pathname) !== null && _a !== void 0 ? _a : '';
        const rec = yield resolveCurrentPage();
        if (!rec)
            return { error: "This page's configuration is read-only (defined in YML)." };
        if (rec.isProtected)
            return { error: PROTECTED_PAGE_ERROR };
        if (rec.type === 'route') {
            const { data: ymlRoutesData } = yield context.executeServiceSafe('System', 'GetRoutesFromConfigurations', { ExcludeOverridden: '0' }, { json: true });
            const ymlRoutes = Array.isArray(ymlRoutesData) ? ymlRoutesData : [];
            const ymlRoute = ymlRoutes.find(r => r.Route === fullPath);
            if (!(ymlRoute === null || ymlRoute === void 0 ? void 0 : ymlRoute.PageID))
                return { error: 'No original view found in YML for this route.' };
            const result = yield handleSaveMainPage(ymlRoute.PageID, {});
            if (result.error)
                return result;
            return { pageId: ymlRoute.PageID, configuredProps: {} };
        }
        const { data: ymlNavData } = yield context.executeServiceSafe('System', 'GetNavigationFromAppConfigurations', {}, { json: true });
        const wrapper = Array.isArray(ymlNavData) ? ymlNavData[0] : ymlNavData;
        const rawNavLinks = wrapper === null || wrapper === void 0 ? void 0 : wrapper.navigationLinks;
        const rawLinks = Array.isArray(rawNavLinks)
            ? rawNavLinks
            : (typeof rawNavLinks === 'string' ? JSON.parse(rawNavLinks) : []);
        const ymlLink = rawLinks.find(l => l.ID === rec.id);
        const originalPageId = (_b = ymlLink === null || ymlLink === void 0 ? void 0 : ymlLink.PageID) !== null && _b !== void 0 ? _b : null;
        if (!originalPageId)
            return { error: 'No original view found in YML for this page.' };
        // IsModified=0 prevents UpdateLink from auto-setting it back to 1
        const result = yield handleSaveMainPage(originalPageId, {}, { IsModified: '0' });
        if (result.error)
            return result;
        setIsMainPageOverridden(false);
        return { pageId: originalPageId, configuredProps: {} };
    }), [context, location === null || location === void 0 ? void 0 : location.pathname, resolveCurrentPage, handleSaveMainPage]);
    useEffect(() => {
        const updatePluggableViews = () => {
            setHasPluggableViews(getAllPluggableViews().length > 0);
        };
        updatePluggableViews();
        const unsubscribe = onRegistryChange(updatePluggableViews);
        return unsubscribe;
    }, []);
    useEffect(() => {
        const update = () => setMountedObjectTypes(getMountedObjectTypes());
        update();
        return onMountedTabsChange(update);
    }, []);
    const exitEditMode = useCallback(() => navigate(generateURL({ removeParams: ['configurepage', 'pe_node', 'pe_mode', 'pe_tab'] })), [navigate, generateURL]);
    useEffect(() => {
        // A system-managed page offers no customisation at all: no pencil, and nothing while the
        // answer is still undefined.
        if (!(canCustomize && hasEditableContent && pageProtection === false))
            return;
        registerFloatingActionSection({
            id: 'page',
            type: FloatingActionSectionType.Page,
            isEditing: isEditing,
            onEnterEditMode: () => navigate(generateURL({ searchParams: { configurepage: '1' } })),
            onExitEditMode: exitEditMode,
            customActions: []
        });
        return () => unregisterFloatingActionSection('page');
    }, [canCustomize, isEditing, hasEditableContent, pageProtection, navigate, generateURL, exitEditMode]);
    return (_jsxs(_Fragment, { children: [canCustomize && isEditing && pageProtection === true && (_jsx("div", { className: "uxpcore_configuredview__protected_notice", children: _jsx(NotificationBlock, { variant: "warning", mode: "compact", layout: "bordered", title: context.$L('uxp-core.page-editor.protected-page-title'), children: _jsxs("div", { className: "uxpcore_configuredview__protected_notice_body", children: [_jsx("span", { children: context.$L('uxp-core.page-editor.protected-page-message') }), _jsx(ButtonComponent, { title: context.$L('uxp-core.text.back'), variant: "secondary", onClick: exitEditMode })] }) }) })), _jsx("div", { className: "uxpcore_configuredview__container", children: componentInfo ? (_jsx(ComponentRenderer, { moduleId: componentInfo.moduleId, componentId: componentInfo.componentId, type: componentInfo.type, configuredProps: configuredProps, additionalProps: additionalProps, notFoundMessage: notFoundMessage, loader: _jsx(ViewSkeleton, {}) })) : (_jsx("div", { className: "uxpcore_configuredview__not_found", children: _jsx(ViewNotFound, { diagnostic: { cause: 'no-ids', id: pageId, kind: 'unknown' }, notFoundMessage: notFoundMessage }) })) }), canCustomize && isEditing && pageProtection === false && (_jsx(PageEditor, { mountedObjectTypes: mountedObjectTypes, mainPage: pageId ? { pageId, configuredProps, isOverridden: isMainPageOverridden, isProtected: isMainPageProtected, isCustom: isMainPageCustom } : undefined, onSaveMainPage: handleSaveMainPage, onResetMainPage: handleResetMainPage }))] }));
};
/**
 * @export
 * A component to render widgets or UI components by parsing the pageId
 *
 * @example
 * <ConfiguredPage
 *   pageId="com.example.module/widget/LocationDetails"
 *   additionalProps={{ LocationKey: 100 }}
 * />
 */
const ConfiguredPage = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(ConfiguredPageBase, Object.assign({}, props)) })));
export default ConfiguredPage;
